import type { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import axios from 'axios'
import { getToken } from './auth'
import { GlobalThis } from './globalThis'
import { cancelAllRequest } from './requestAbortController'
// import { axiosCacheAdapter } from './axiosCacheAdapter'

// 定义请求响应参数，不含data
interface Result {
  code: number
  msg: string
  success?: boolean
  data?: any
  message?: string
}

// 请求响应参数，包含data
interface ResultData<T = any> extends Result {
  [x: string]: any
  data?: T
  results?: Array<T>
  totalPages?: T
  totalElements?: T
  records?: Array<T>
  pages?: number
}
enum RequestEnums {
  TIMEOUT = 600000,
  SUCCESS = 200,
}
const config = {
  baseURL: import.meta.env.VITE_BASE_URL,
  timeout: RequestEnums.TIMEOUT as number,
  withCredentials: true,
}
class RequestHttp {
  service: AxiosInstance
  lastMessageData = {
    message: '',
    timestamp: 0,
  }

  public constructor(config: AxiosRequestConfig) {
    this.service = axios.create(config)
    // this.service.defaults.adapter = axiosCacheAdapter
    this.service.interceptors.request.use(
      (config: AxiosRequestConfig) => {
        const token = getToken()
        let headers: any = {
          'Content-Type': 'application/json;charset=utf-8',
          Authorization: token,
          'User-Type': 'BUYER',
          locale: Local.get('LANG') || 'en',
          ...(config?.headers || {}),
        }
        if (config?.headers?.custom || config?.headers?.custom === 'true') {
          delete config?.headers.custom
          headers = config?.headers || {}
        }
        //  字符串 true 自定义上传用
        if (config?.headers?.isSeller || config?.headers?.isSeller === 'true') {
          headers = {
            ...headers,
            'User-Type': 'SELLER',
            'Shop-Domain': Local.get('creatorDomain') || Cookie.get('creatorDomain') || '',
            ...(config?.headers || {}),
          }
        }
        delete headers.isSeller
        if (config?.headers?.invitorDomain) {
          headers.invitorDomain = config?.headers?.invitorDomain
        }
        // promotionTag 为运营推广注册标识
        if (config?.headers?.promotionTag) {
          headers['Promotion-Tag'] = config?.headers?.promotionTag
          delete headers.promotionTag
        }

        //  inject abortcontroller signal
        if (config?.abortControllerKey) {
          const controller = new AbortController()
          config.signal = controller.signal
          saveRequestByKey(config.abortControllerKey, controller)
        }
        return {
          ...config,
          headers,
        }
      },
      (error: AxiosError) => {
        Promise.reject(error)
      },
    )

    this.service.interceptors.response.use(
      (response: AxiosResponse) => {
        const { data, request } = response
        const { tgLogin } = useLogin()
        const { isTelegram } = useTelegram()
        if (data.code === 302 || request?.responseURL.includes('forbidden.html')) {
          window.location.replace(data.msg)
          return
        }
        if ([401].includes(data.code)) {
          // 取消所有正在排队的请求
          cancelAllRequest()
          if (isTelegram) {
            tgLogin()
          } else {
            const userStore = useUserStore()
            userStore.CLEAR()
          }
          return Promise.reject(new Error(data.msg || 'Unauthorized'))
        }
        if (data.success) {
          return data.data
        } else {
          if (data.code === 1012220) {
            GlobalThis.$globalAlert.open(data.msg || data.message, 'SUPPORT', () => {
              // 点击按钮 唤起 邮件系统 Support@alchemypay.org
              window.open('mailto:Support@alchemypay.org')
            })
            return Promise.reject(new Error(data.msg || data.message))
          } else if (
            ![
              '/dfans/v1/global/login',
              '/kyc-task/v1/detail',
              '/dfans/v1/login/password',
              '/dfans/v1/register/password',
              '/shop/v1/domain/exist',
            ].includes(response!.config!.url || '')
          ) {
            const message =
              data.msg || data.message || 'There seems to be a problem. Please try again.'
            if (
              this.lastMessageData.message !== message ||
              Date.now() - this.lastMessageData.timestamp > 3000
            ) {
              if (
                ![
                  1012108, 101029, 101038, 1012049, 1012048, 101040, 101041, 101011, 101006,
                  1012027,
                ].includes(data.code)
              ) {
                const { addTrack } = useReport()
                addTrack({
                  event: 'error_network',
                  extra: {
                    message: message,
                    code: data.code,
                    body: response!.config!.data,
                    url: response!.config!.url,
                    response: response,
                  },
                  page: window.location.origin + window.location.pathname,
                  referrer: window.location.origin + window.location.pathname,
                  timestamp: Date.now(),
                })
                if (
                  !['/event/notification/statistic/unread'].includes(response!.config!.url || '')
                ) {
                  GlobalThis.$globalAlert.open(`${message}(No.${data.code}.)`)
                }
              }
            }
            this.lastMessageData.timestamp = Date.now()
            this.lastMessageData.message = message
          }
          return Promise.reject(data)
        }
      },
      (error: AxiosError) => {
        if (error.code === 'ERR_CANCELED') {
          // ignore cancel request
          error.config?.onRequestCanceled?.()
        } else if (error.code === 'ERR_NETWORK') {
          return Promise.reject(error)
        } else {
          const message = error.message || 'There seems to be a problem. Please try again.'
          if (
            this.lastMessageData.message !== message ||
            Date.now() - this.lastMessageData.timestamp > 3000
          ) {
            const { addTrack } = useReport()
            addTrack({
              event: 'error_network',
              extra: {
                type: 'error_catch',
                message: message,
                code: error.code,
                body: error.config!.data,
                url: error.config!.url,
                response: error,
              },
              page: window.location.origin + window.location.pathname,
              referrer: window.location.origin + window.location.pathname,
              timestamp: Date.now(),
            })
            if (!['/event/notification/statistic/unread'].includes(error.config!.url || '')) {
              GlobalThis.$globalAlert.open(`${message}(No.${error.code}.)`)
            }
          }
          this.lastMessageData.timestamp = Date.now()
          this.lastMessageData.message = message
          return Promise.reject(error)
        }
      },
    )
  }

  get<T>(url: string, params?: object, config?: any): Promise<ResultData<T>> {
    return this.service.get(url, {
      params,
      ...(config || {}),
    })
  }

  post<T>(url: string, params?: object, config?: object): Promise<ResultData<T>> {
    return this.service.post(url, params, config)
  }

  put<T>(url: string, params?: object, config?: object): Promise<ResultData<T>> {
    return this.service.put(url, params, config)
  }

  delete<T>(url: string, params?: object, config?: object): Promise<ResultData<T>> {
    return this.service.delete(url, { data: params, ...config })
  }
}

export default new RequestHttp(config)
